import React from "react";
import { useSortable } from '@dnd-kit/sortable';
import { MdReorder, MdDelete } from 'react-icons/md';
import { Box, IconButton, FormControl, InputLabel, MenuItem, Select, Grid, Paper } from "@mui/material";
import { CSS } from '@dnd-kit/utilities';

const DragHandle = (props) => (
  <Box {...props.listeners} {...props.attributes} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <MdReorder />
  </Box>
);

const SuggestedSearchSortableItem = ({ data, data_options, handleChange, handleDelete, el_index }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: data.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '100%',
  };

  const renderObjectOptions = () => {
    const objects = ["AUTHOR", "SESSION", "SERIES", "AUDIOBOOK", "CHALLENGE", "QUICK_TAP_SESSION", "CARD_DECK"];
    return objects.map((el) => (
      <MenuItem key={el} value={el}>
        {el}
      </MenuItem>
    ));
  };

  const renderSearchOptions = () => {
    const searchOptions = ["REDIRECT", "SEARCH"];
    return searchOptions.map((el) => (
      <MenuItem key={el} value={el}>
        {el}
      </MenuItem>
    ));
  };

  const renderDataOption = () => {
    if (!data_options || typeof data_options !== 'object') return [];

    switch (data.object_type) {
      case "AUTHOR":
        return data_options.authors?.map((el) => (
          <MenuItem key={el.author_id} value={el.author_id}>
            {el.author_name}
          </MenuItem>
        )) || [];
      case "SESSION":
        return data_options.sessions?.map((el) => (
          <MenuItem key={el.session_id} value={el.session_id}>
            {el.session_name}
          </MenuItem>
        )) || [];
      case "SERIES":
        return data_options.series?.map((el) => (
          <MenuItem key={el.series_id} value={el.series_id}>
            {el.series_title}
          </MenuItem>
        )) || [];
      case "AUDIOBOOK":
        return data_options.audiobook?.map((el) => (
          <MenuItem key={el.subcategory_id} value={el.subcategory_id}>
            {el.subcategory_title}
          </MenuItem>
        )) || [];
      case "CHALLENGE":
        return data_options.challenges?.map((el) => (
          <MenuItem key={el.challenge_id} value={el.challenge_id}>
            {el.challenge_title}
          </MenuItem>
        )) || [];
      case "QUICK_TAP_SESSION":
        return data_options.quick_tap_sessions?.map((el) => (
          <MenuItem key={el.quick_tap_session_id} value={el.quick_tap_session_id}>
            {el.quick_tap_title}
          </MenuItem>
        )) || [];
      case "CARD_DECK":
        return data_options.card_decks?.map((el) => (
          <MenuItem key={el.deck_id} value={el.deck_id}>
            {el.deck_name}
          </MenuItem>
        )) || [];
      default:
        return [];
    }
  };

  const getCurrentObjectValue = () => {
    const options = renderDataOption();
    const currentValue = data.object_id;

    // If no options are available, return empty string
    if (options.length === 0) return "";

    // Check if current value exists in options
    const valueExists = options.some(option => option.props.value === currentValue);
    return valueExists ? currentValue : "";
  };

  const commonSelectStyles = {
    '& .MuiSelect-select': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingTop: '8px',
      paddingBottom: '8px',
      height: '20px',
      lineHeight: '20px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)'
    }
  };

  return (
    <Paper
      ref={setNodeRef}
      elevation={1}
      sx={{
        ...style,
        p: 1.5,
        mb: 1.5,
        '&:hover': {
          bgcolor: 'action.hover'
        }
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ width: '100%', m: 0 }}
      >
        {/* Drag Handle - moved to left */}
        <Grid flex={0.5} sx={{ display: 'flex', justifyContent: 'center', pl: '12px !important' }}>
          <DragHandle listeners={listeners} attributes={attributes} />
        </Grid>

        {/* Object Type Select */}
        <Grid flex={3.5}>
          <FormControl fullWidth>
            <InputLabel>Object Type</InputLabel>
            <Select
              value={data.object_type || ""}
              onChange={(e) => handleChange({ value: e.target.value, prop: "object_type" }, el_index)}
              label="Object Type"
              sx={commonSelectStyles}
            >
              {renderObjectOptions()}
            </Select>
          </FormControl>
        </Grid>

        {/* Object Name/Title Select */}
        <Grid flex={6}>
          <FormControl fullWidth>
            <InputLabel>Object Name/Title</InputLabel>
            {renderDataOption().length > 0 ? (
              <Select
                value={getCurrentObjectValue()}
                onChange={(e) => handleChange({ value: e.target.value, prop: "object_id" }, el_index)}
                label="Object Name/Title"
                sx={commonSelectStyles}
              >
                {renderDataOption()}
              </Select>
            ) : (
              <Select
                value=""
                label="Object Name/Title"
                disabled
                sx={commonSelectStyles}
              >
                <MenuItem value="">Loading options...</MenuItem>
              </Select>
            )}
          </FormControl>
        </Grid>

        {/* Search Type Select */}
        <Grid flex={2.5}>
          <FormControl fullWidth>
            <InputLabel>Search Type</InputLabel>
            <Select
              value={data.search_behavior || ""}
              onChange={(e) => handleChange({ value: e.target.value, prop: "search_behavior" }, el_index)}
              label="Search Type"
              sx={commonSelectStyles}
            >
              {renderSearchOptions()}
            </Select>
          </FormControl>
        </Grid>

        {/* Delete Button */}
        <Grid flex={0.5} sx={{ display: 'flex', justifyContent: 'center', pr: '12px !important' }}>
          <IconButton
            color="error"
            onClick={() => handleDelete(data.id, el_index)}
            size="small"
          >
            <MdDelete />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SuggestedSearchSortableItem;
